import React, { useRef, useEffect, useCallback, useMemo, memo } from "react";
import { TrackedObjectsWithDetections } from "../../../types/TrackedObject";

interface BoudningBoxOverlayProps {
    videoRef: React.RefObject<HTMLVideoElement>;
    data: TrackedObjectsWithDetections[];
    targetFPS?: number;
    enable: boolean;
}

function getCurrentFrame(
    videoEl: HTMLVideoElement | null,
    fps: number,
): number {
    if (!videoEl) return 0;
    return Math.round(videoEl.currentTime * fps);
}

function createBoundingBoxWorker() {
    return new Worker(new URL("./boundingBoxWorker.js", import.meta.url));
}

export const BoundingBoxOverlay: React.FC<BoudningBoxOverlayProps> = ({
    videoRef,
    data,
    targetFPS = 29.97,
    enable = false,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const workerRef = useRef<Worker | null>(null);

    useEffect(() => {
        if (!enable) return;
        if (!videoRef.current || !canvasRef.current || !data) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        // Always set canvas size from parent before posting to worker
        const parent = video.parentElement;
        if (parent) {
            canvas.width = parent.offsetWidth;
            canvas.height = parent.offsetHeight;
        }

        if (!workerRef.current) {
            workerRef.current = createBoundingBoxWorker();
        }
        const worker = workerRef.current;

        let lastFrame = -1;
        let animationFrameId: number;

        if (worker) {
            worker.onmessage = (e) => {
                // Draw only when new data arrives from worker
                const parent = video.parentElement;
                if (parent) {
                    canvas.width = parent.offsetWidth;
                    canvas.height = parent.offsetHeight;
                }
                if (!ctx) return;
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                e.data.forEach((box: any) => {
                    ctx.strokeStyle = "lime";
                    ctx.lineWidth = 2;
                    ctx.strokeRect(box.x, box.y, box.width, box.height);
                    ctx.font = "14px Arial";
                    ctx.fillStyle = "lime";
                    ctx.fillText(String(box.ordinal), box.x, box.y - 4);
                });
            };
        }

        const onFrame = () => {
            const frame = getCurrentFrame(video, targetFPS);
            if (frame !== lastFrame && worker) {
                lastFrame = frame;
                console.log(data);
                worker.postMessage({
                    data,
                    frame,
                    videoWidth: video.videoWidth,
                    videoHeight: video.videoHeight,
                    canvasWidth: canvas.width,
                    canvasHeight: canvas.height,
                });
            }
            animationFrameId = requestAnimationFrame(onFrame);
        };
        onFrame();
        return () => {
            const ctx = canvas.getContext("2d");
            if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
            cancelAnimationFrame(animationFrameId);
        };
    }, [videoRef, data, targetFPS]);

    // Canvas overlay, positioned absolutely over video
    return (
        <canvas
            ref={canvasRef}
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: 10,
            }}
        />
    );
};
